import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@cortex/web-design-system/components/ui/Modal';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { usePmoSupplyRateProjects } from '../../../hooks/usePmoFlow.js';
import {
  SupplyRateTeamBreakdown,
  SupplyRateTeamPill,
  toSupplyRateSegment,
} from './SupplyRateTeamBreakdown.js';
import type { SupplyRateSegmentKey } from './SupplyRateTeamBreakdown.js';
import { BACKLOG_BASE } from '../project/index.js';
import { formatPt } from '../../../lib/format.js';
import type { PmoSupplyRatePrepRequirement, PmoSupplyRateStage } from '../../../types/pmo-flow.js';

export interface SupplyRateProjectsModalProps {
  /** モーダル表示状態 */
  open: boolean;
  /** 閉じるコールバック */
  onClose: () => void;
  /** 明細を引く段。カードごとに異なる */
  stage: PmoSupplyRateStage;
}

/**
 * ISO8601 日時から日付部分（YYYY-MM-DD）だけを取り出す。null はハイフン表示。
 * @graph-connects none
 */
function toDate(iso: string | null): string {
  return iso ? iso.slice(0, 10) : '-';
}

/**
 * ざっくり見積のレンジ表示（例: 12 〜 22）。未入力は null を返す。
 * @graph-connects none
 */
function formatRange(min: number | null, max: number | null): string | null {
  if (min === null || max === null) return null;
  return min === max ? formatPt(min) : `${formatPt(min)} 〜 ${formatPt(max)}`;
}

/**
 * 要否つき準備子票の 1 セル。
 *
 * 「不要」と「必要だがまだ着手していない」は意味が違うので同じ見た目にしない。
 * 不要はバッジを出さず淡色テキストで沈め、必要な案件だけステータスをバッジで前に出す。
 * 必要なのに子票が無い場合は運用の抜けなので警告色で見せる。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business 案件明細の準備子票セル。要否が不要なら「不要」、必要なら子票ステータスをバッジ表示し、子票未作成は警告色で示す
 * @graph-connects Badge [calls] ステータスバッジ描画
 */
function PrepRequirementCell({
  requirement,
  label,
}: {
  requirement: PmoSupplyRatePrepRequirement;
  label: { notRequired: string; unknown: string; notCreated: string };
}) {
  if (requirement.requirement === 'notRequired') {
    return <span className="text-[var(--color-text-muted)]">{label.notRequired}</span>;
  }
  if (requirement.requirement === 'unknown') {
    return <span className="text-[var(--color-text-muted)]">{label.unknown}</span>;
  }
  if (requirement.status === null) {
    return (
      <Badge variant="warning" size="sm">
        {label.notCreated}
      </Badge>
    );
  }
  return (
    <Badge variant={requirement.status === '完了' ? 'success' : 'default'} size="sm">
      {requirement.status}
    </Badge>
  );
}

/**
 * 充足率カードの案件・バグドリルダウン。分子に乗っている案件・バグそのものを
 * ざっくり見積の大きい順で並べる（アサイン可能段は案件とバグを含む）。
 *
 * 見積未入力の案件は 0pt として分子に乗っている＝率を押し下げている当事者なので、
 * ハイフンで済ませず「見積なし」バッジで明示し、どれを埋めれば率が動くか分かるようにする。
 *
 * 着手予定段では内訳のセグメントを押すとその区分だけに一覧を絞り込む。絞り込みは
 * **一覧だけに掛け、内訳と見出しの件数・合計pt は段の全件のまま**にする。内訳まで
 * 絞ると分母が動いて区分どうしを比べられなくなり、「全体のどこを見ているか」を
 * 見失うため。いま何件を見ているかは一覧の上の絞り込み行で別に示す。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PMO充足率の案件・バグドリルダウンモーダル。案件/バグのキー・件名・事業・担当チーム・ざっくり見積・計上pt・起票日を一覧表示し、見積未入力の案件を明示する。業務フロー図作成/バリデーションリスト作成の要否の列は要件定義2段でのみ表示し、開発着手予定段では冒頭にチーム別プール内訳と開発リーダー列を出したうえで、内訳のセグメントのクリックで一覧をその区分の案件だけに絞り込む
 * @graph-connects usePmoSupplyRateProjects [calls] 指定した段の案件・バグ明細取得
 * @graph-connects PrepRequirementCell [calls] 準備子票の要否と状況の描画
 * @graph-connects SupplyRateTeamBreakdown [calls] 着手予定プールのチーム別内訳の描画と区分の絞り込み操作
 * @graph-connects SupplyRateTeamPill [calls] 案件のプール区分のカラーpill描画
 * @graph-connects toSupplyRateSegment [calls] 案件のプール区分解決
 * @graph-connects Modal [calls] 汎用モーダルで内容を表示
 * @graph-connects Badge [calls] ステータス/見積なしバッジ描画
 */
export function SupplyRateProjectsModal({ open, onClose, stage }: SupplyRateProjectsModalProps) {
  const { t } = useTranslation();
  const { data, isLoading } = usePmoSupplyRateProjects(stage, open);
  const [selectedSegment, setSelectedSegment] = useState<SupplyRateSegmentKey | null>(null);

  // 別の段を開く / 閉じるたびに絞り込みを落とす。モーダルは開閉で unmount されないため、
  // 残っていると次に開いたときに「なぜか一部しか出ていない」状態から始まってしまう
  useEffect(() => {
    setSelectedSegment(null);
  }, [stage, open]);

  // 準備子票の列は「この先どの成果物を作る必要があるか」を見るためのものなので、要件定義が
  // まだ動いていない 要件定義 2 段でだけ出す（ステータスは定義上すべて未対応なので、
  // ここで読む価値があるのは要否のほう）。開発 2 段は総合テスト作成の完了が条件に
  // 入っていて情報量がない
  const showPrepColumns = stage === 'requirementAssignable' || stage === 'requirementPlannedStart';
  // 開発リーダーは アサイン確定済みの 開発着手予定 段でしか埋まらない。他の段で出すと
  // ほぼ全件が空欄・その他になり、内訳が「未アサインだから未分類」としか読めない列になる
  const showTeamBreakdown = stage === 'plannedStart';
  const prepLabels = {
    notRequired: t('kpi.pmoFlow.supplyRate.projectsModal.notRequired'),
    unknown: t('kpi.pmoFlow.supplyRate.projectsModal.requirementUnknown'),
    notCreated: t('kpi.pmoFlow.supplyRate.projectsModal.notCreated'),
  };

  const projects = data?.projects ?? [];
  // 合計は段の計上pt（要件定義 2 段は見積平均の 15%）で出す。カードの段の pt と一致させる
  const totalPoints = projects.reduce((sum, p) => sum + (p.countedPoint ?? 0), 0);

  // 内訳を出さない段には絞り込みの操作子が無いので、状態が残っていても効かせない
  const activeSegment = showTeamBreakdown ? selectedSegment : null;
  const visibleProjects =
    activeSegment === null
      ? projects
      : projects.filter((project) => toSupplyRateSegment(project) === activeSegment);
  const visiblePoints = visibleProjects.reduce((sum, p) => sum + (p.countedPoint ?? 0), 0);

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="full"
      // 列が増える段（要件定義 2 段の準備子票 2 列 / 開発着手予定の開発リーダー列）だけ広げ、
      // ステータスのラベルを折り返さずに出す
      className={showPrepColumns || showTeamBreakdown ? 'md:max-w-[88rem]' : undefined}
      title={t(`kpi.pmoFlow.supplyRate.projectsModal.title.${stage}`)}
      description={t(`kpi.pmoFlow.supplyRate.projectsModal.description.${stage}`, {
        count: projects.length,
        totalPt: formatPt(totalPoints),
      })}
    >
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="loading-spinner" />
        </div>
      ) : projects.length === 0 ? (
        <div className="py-12 text-center text-[var(--color-text-muted)]">
          {t(`kpi.pmoFlow.supplyRate.projectsModal.noItems.${stage}`)}
        </div>
      ) : (
        <>
          {showTeamBreakdown && (
            <SupplyRateTeamBreakdown
              projects={projects}
              selectedSegment={selectedSegment}
              onSelectSegment={setSelectedSegment}
            />
          )}
          {activeSegment !== null && (
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <SupplyRateTeamPill
                tone={activeSegment}
                label={t(
                  `kpi.pmoFlow.supplyRate.projectsModal.teamBreakdown.segment.${activeSegment}`,
                )}
              />
              <span className="text-xs text-[var(--color-text-muted)]">
                {t('kpi.pmoFlow.supplyRate.projectsModal.teamBreakdown.filterActive', {
                  count: visibleProjects.length,
                  totalPt: formatPt(visiblePoints),
                })}
              </span>
              <button
                type="button"
                onClick={() => {
                  setSelectedSegment(null);
                }}
                className="rounded-md border border-[var(--color-border)] px-2 py-0.5 text-xs text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-bg-hover)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--color-border-focus)]"
              >
                {t('kpi.pmoFlow.supplyRate.projectsModal.teamBreakdown.filterClear')}
              </button>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--color-border)]">
                  <th className="whitespace-nowrap px-3 py-2 text-left font-medium text-[var(--color-text-muted)]">
                    {t('kpi.pmoFlow.supplyRate.projectsModal.issueKey')}
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-[var(--color-text-muted)]">
                    {t('kpi.pmoFlow.supplyRate.projectsModal.summary')}
                  </th>
                  <th className="whitespace-nowrap px-3 py-2 text-left font-medium text-[var(--color-text-muted)]">
                    {t('kpi.pmoFlow.supplyRate.projectsModal.business')}
                  </th>
                  <th className="whitespace-nowrap px-3 py-2 text-left font-medium text-[var(--color-text-muted)]">
                    {t('kpi.pmoFlow.supplyRate.projectsModal.team')}
                  </th>
                  {showTeamBreakdown && (
                    <th className="whitespace-nowrap px-3 py-2 text-left font-medium text-[var(--color-text-muted)]">
                      {t('kpi.pmoFlow.supplyRate.projectsModal.devLeader')}
                    </th>
                  )}
                  <th className="whitespace-nowrap px-3 py-2 text-left font-medium text-[var(--color-text-muted)]">
                    {t('kpi.pmoFlow.supplyRate.projectsModal.status')}
                  </th>
                  <th className="whitespace-nowrap px-3 py-2 text-right font-medium text-[var(--color-text-muted)]">
                    {t('kpi.pmoFlow.supplyRate.projectsModal.roughRange')}
                  </th>
                  <th className="whitespace-nowrap px-3 py-2 text-right font-medium text-[var(--color-text-muted)]">
                    {t('kpi.pmoFlow.supplyRate.projectsModal.roughPoint')}
                  </th>
                  {showPrepColumns && (
                    <>
                      <th className="whitespace-nowrap px-3 py-2 text-left font-medium text-[var(--color-text-muted)]">
                        {t('kpi.pmoFlow.supplyRate.projectsModal.businessFlow')}
                      </th>
                      <th className="whitespace-nowrap px-3 py-2 text-left font-medium text-[var(--color-text-muted)]">
                        {t('kpi.pmoFlow.supplyRate.projectsModal.validationList')}
                      </th>
                    </>
                  )}
                  <th className="whitespace-nowrap px-3 py-2 text-right font-medium text-[var(--color-text-muted)]">
                    {t('kpi.pmoFlow.supplyRate.projectsModal.createdAt')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {visibleProjects.map((project) => {
                  const range = formatRange(project.roughPointMin, project.roughPointMax);
                  return (
                    <tr
                      key={project.issueKey}
                      className="border-b border-[var(--color-border)] last:border-b-0 hover:bg-[var(--color-bg-hover)]"
                    >
                      <td className="whitespace-nowrap px-3 py-2">
                        <a
                          href={`${BACKLOG_BASE}${project.issueKey}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[var(--color-accent-primary)] hover:underline"
                        >
                          {project.issueKey}
                        </a>
                      </td>
                      <td className="px-3 py-2 text-[var(--color-text-primary)]">
                        <div className="line-clamp-2">{project.summary}</div>
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-[var(--color-text-secondary)]">
                        {project.business ?? '-'}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-[var(--color-text-secondary)]">
                        {project.responsibleTeam ?? '-'}
                      </td>
                      {showTeamBreakdown && (
                        <td className="whitespace-nowrap px-3 py-2 text-[var(--color-text-secondary)]">
                          <span className="flex items-center gap-1.5">
                            {project.devLeader ?? '-'}
                            {/*
                              区分は内訳と同じ解決関数を通す。バーのどのセグメントに
                              属する行なのかを、pill の名前と色の両方で辿れるようにする
                            */}
                            <SupplyRateTeamPill
                              tone={toSupplyRateSegment(project)}
                              label={t(
                                `kpi.pmoFlow.supplyRate.projectsModal.teamBreakdown.segment.${toSupplyRateSegment(project)}`,
                              )}
                            />
                          </span>
                        </td>
                      )}
                      <td className="whitespace-nowrap px-3 py-2 text-[var(--color-text-secondary)]">
                        {project.status ?? '-'}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-right font-mono tabular-nums text-[var(--color-text-secondary)]">
                        {range ?? '-'}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-right">
                        {project.countedPoint !== null ? (
                          <span className="font-mono font-bold tabular-nums text-[var(--color-text-primary)]">
                            {formatPt(project.countedPoint)}
                          </span>
                        ) : (
                          <Badge variant="warning" size="sm">
                            {t('kpi.pmoFlow.supplyRate.projectsModal.noEstimate')}
                          </Badge>
                        )}
                      </td>
                      {showPrepColumns && (
                        <>
                          <td className="whitespace-nowrap px-3 py-2">
                            <PrepRequirementCell
                              requirement={project.businessFlow}
                              label={prepLabels}
                            />
                          </td>
                          <td className="whitespace-nowrap px-3 py-2">
                            <PrepRequirementCell
                              requirement={project.validationList}
                              label={prepLabels}
                            />
                          </td>
                        </>
                      )}
                      <td className="whitespace-nowrap px-3 py-2 text-right font-mono tabular-nums text-[var(--color-text-secondary)]">
                        {toDate(project.createdAt)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Modal>
  );
}
